import { useCallback, useEffect, useState } from 'react';
import { api } from './api';

const EMPTY_STEP = { completed: false, auto: false, skipped: false };
const DEFAULT_STATE = {
    dismissed: false,
    counters: { menu_items: 0, tables: 0, staff: 0 },
    steps: {
        brand: { ...EMPTY_STEP },
        menu: { ...EMPTY_STEP },
        table: { ...EMPTY_STEP },
        scan_qr: { ...EMPTY_STEP },
        invite_team: { ...EMPTY_STEP },
    },
};

export const ONBOARDING_STEP_KEYS = ['brand', 'menu', 'table', 'scan_qr', 'invite_team'];

export function countCompleted(state) {
    if (!state?.steps) return 0;
    return ONBOARDING_STEP_KEYS.reduce(
        (n, k) => n + (state.steps[k]?.completed ? 1 : 0),
        0,
    );
}

export function useOnboarding({ enabled = true } = {}) {
    const [state, setState] = useState(DEFAULT_STATE);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);

    const fetchState = useCallback(async () => {
        try {
            const { data } = await api.get('/r/onboarding/state');
            setState({ ...DEFAULT_STATE, ...data, steps: { ...DEFAULT_STATE.steps, ...(data?.steps || {}) } });
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!enabled) { setLoading(false); return; }
        fetchState();
    }, [enabled, fetchState]);

    const dismiss = useCallback(async () => {
        try {
            const { data } = await api.post('/r/onboarding/dismiss');
            setState((prev) => ({ ...prev, ...data }));
        } catch (err) {
            setError(err);
        }
    }, []);

    const reopen = useCallback(async () => {
        try {
            const { data } = await api.post('/r/onboarding/reopen');
            setState((prev) => ({ ...prev, ...data }));
        } catch (err) {
            setError(err);
        }
    }, []);

    const skipStep = useCallback(async (key) => {
        try {
            const { data } = await api.post(`/r/onboarding/step/${key}/skip`);
            setState((prev) => ({ ...prev, ...data }));
        } catch (err) {
            setError(err);
        }
    }, []);

    const completed = countCompleted(state);
    const total = ONBOARDING_STEP_KEYS.length;
    const allDone = completed >= total;

    return {
        state, loading, error,
        completed, total, allDone,
        fetchState, dismiss, reopen, skipStep,
    };
}
